import { AclEntity } from '@agilelab/plugin-wb-access-control-common';
import {
  ComponentInstanceEntity,
  ConsumableMode,
  InstanceEntity,
} from '@agilelab/plugin-wb-marketplace-common';
import { CustomViewCtrls } from '@agilelab/plugin-wb-platform';
import { anyCaseToCapitalCase } from '@agilelab/plugin-wb-platform-common';

enum AccessStatus {
  NON_CONSUMABLE = 'non_consumable',
  ACCESS = 'access',
  NO_ACCESS = 'no_access',
  PARTIAL_ACCESS = 'partial_access',
}

type ComponentState = {
  state: string;
  summary: AccessStatus;
  description: string;
  childrenStates?: AccessStatus[];
};

CustomViewCtrls.register({
  id: 'marketplace_system',
  function: ({
    dpData,
    componentsList,
    selectedComponents,
    consumableInterfaceTypeField,
    kindsLabels,
    multipleAccessRequestSelectionEnabled,
    legacyTechnicalInformationConfig,
  }: {
    dpData: InstanceEntity;
    componentsList: ComponentInstanceEntity[] | undefined;
    selectedComponents: ComponentInstanceEntity[];
    consumableInterfaceTypeField: string;
    kindsLabels: Record<string, string>;
    multipleAccessRequestSelectionEnabled: boolean;
    legacyTechnicalInformationConfig: boolean;
  }) => {
    const orderFn = (a: any, b: any) => {
      if (a.type < b.type) return -1;
      if (a.type > b.type) return 1;
      return 0;
    };
    const components = componentsList
      ?.map(component => ({
        ...component,
        selected: selectedComponents.some(s => s.id === component.id),
        access: computeAccessFlag(component),
        accessStatus: computeAccessStatus(component),
        type:
          typeof component.descriptor[consumableInterfaceTypeField] === 'string'
            ? anyCaseToCapitalCase(
                component.descriptor[consumableInterfaceTypeField],
              )
            : undefined,
      }))
      .sort(orderFn);
    const kinds = components
      ?.map(o => o.descriptor.kind.toLowerCase() || '')
      .filter((val, idx, list) => list.indexOf(val) === idx);
    const componentsByKind = kinds?.map(kind =>
      Object.assign(
        { kind, label: kindsLabels[kind] || kind },
        {
          components: components
            ?.filter(o => o.descriptor.kind.toLowerCase() === kind)
            .sort(orderFn),
        },
      ),
    );
    const data = {
      ...dpData.descriptor,
      _computedInfo: {
        ...dpData,
        // add multipleAccessRequestSelectionEnabled flag to allow rendering of table as radio or checkboxes
        multipleAccessRequestSelectionEnabled,
        // add legacyTechnicalInformationConfig to allow custom view to read legacy technical information based on legacy config in marketplace_tech_card component, will remove in the future
        legacyTechnicalInformationConfig,
      },
      _selectedComponents: selectedComponents,
      _componentsByKind: componentsByKind,
      _components: components,
    } as Record<string, any>;
    if (!data.version) data.version = dpData.version;
    return data;
  },
});

/**
 * The logged user has access to a component if:
 * - the component is consumable and the user is part of its ACL
 * - the component is not consumable but it has at least one subcomponent that is consumable and the user is part of its ACL
 *
 * @todo This function assumes that `acls` within the provided `component` are relative to the logged user. Review this assumption.
 */
function computeAccessFlag(component: ComponentInstanceEntity): boolean {
  const components = [{ data: component }, ...(component.components ?? [])];

  return components.some(
    c =>
      c.data.consumable === ConsumableMode.Consumable && !!c.data.acls?.length,
  );
}

function generateDescription(
  status: AccessStatus,
  subComponentsCount?: { accessible: number; total: number },
): string {
  switch (status) {
    case AccessStatus.NON_CONSUMABLE:
      return 'This component is not consumable, and hence, it cannot be accessed';
    case AccessStatus.NO_ACCESS:
      return 'You do not have access to this component';
    case AccessStatus.PARTIAL_ACCESS:
      return subComponentsCount
        ? `You have partial access to this component: you can access ${subComponentsCount?.accessible}/${subComponentsCount.total} of its subcomponents`
        : 'You have partial access to this component';
    case AccessStatus.ACCESS:
      return 'You have access to this component';
    default:
      return 'Unknown access status';
  }
}

function computeComponentAccessStatus(c: {
  consumable?: ConsumableMode;
  acls?: AclEntity[];
}): AccessStatus {
  if (c.consumable !== ConsumableMode.Consumable) {
    return AccessStatus.NON_CONSUMABLE;
  }
  if (c.acls && c.acls.length > 0) {
    return AccessStatus.ACCESS;
  }
  return AccessStatus.NO_ACCESS;
}

function computeAccessStatus(cn: ComponentInstanceEntity): ComponentState {
  const state = computeComponentAccessStatus(cn);
  const childrenStates: AccessStatus[] = (cn.components ?? [])
    .map(c => computeComponentAccessStatus(c.data))
    .filter(c => c !== AccessStatus.NON_CONSUMABLE);
  let summary = state;
  let description = generateDescription(state);
  if (childrenStates.length > 0) {
    if (
      state === AccessStatus.NO_ACCESS ||
      state === AccessStatus.NON_CONSUMABLE
    ) {
      const childrenAccessCount = childrenStates.filter(
        c => c === AccessStatus.ACCESS,
      ).length;
      if (childrenAccessCount > 0) {
        summary =
          childrenAccessCount === childrenStates.length
            ? AccessStatus.ACCESS
            : AccessStatus.PARTIAL_ACCESS;
        description = generateDescription(summary, {
          accessible: childrenAccessCount,
          total: childrenStates.length,
        });
      }
    }
  }
  return {
    state,
    childrenStates,
    summary,
    description,
  };
}
